import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SaveButton, DiscardButton } from '@components/common';

type Props = {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
};

export const AcknowledgmentConfigHeader = ({
  hasUnsavedChanges,
  isSaving,
  onSave,
  onDiscard
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: 'background.default',
        borderBottom: '1px solid',
        borderBottomColor: 'divider'
      }}
    >
      <Box
        sx={{
          padding: { xs: 1.5, sm: 3 },
          maxWidth: 1200,
          margin: '0 auto'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: 1 },
              minWidth: 0,
              flex: { xs: 'none', sm: 1 }
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant={isMobile ? 'h6' : 'h4'}
                component="h1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '18px', sm: '34px' },
                  lineHeight: { xs: '24px', sm: '42px' },
                  letterSpacing: { xs: '0.15px', sm: '0.25px' },
                  color: isMobile ? 'text.primary' : theme.palette.primary.main
                }}
              >
                {t('settings:customerAcknowledgment.title')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t('settings:customerAcknowledgment.description')}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-end', sm: 'flex-start' }
            }}
          >
            {hasUnsavedChanges && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'warning.main',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 }
                      },
                      animation: 'pulse 2s infinite'
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'settings:allowedEvents.header.unsavedChanges',
                      'Unsaved changes'
                    )}
                  </Typography>
                </Box>

                <DiscardButton
                  onClick={onDiscard}
                  disabled={isSaving}
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 }
                  }}
                >
                  {t('settings:allowedEvents.header.discard')}
                </DiscardButton>

                <SaveButton
                  onClick={onSave}
                  loading={isSaving}
                  disabled={isSaving}
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 1 }
                  }}
                >
                  {isSaving
                    ? t('settings:allowedEvents.header.saving')
                    : t('settings:allowedEvents.header.save')}
                </SaveButton>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
